import type {
  SDKBridgeCallback, SDKBridgeNativeMessage, SDKBridgeNativeReturn, SDKBridgeSHandler,
} from "./type";

declare global {
  interface Window {
    dscb: number;
    _dsbridge?: { call: (method: string, args: string) => string };
    _dswk?: boolean;
    _dsInit?: boolean;
    _dsf: Record<string, SDKBridgeSHandler>;
    _dsaf: Record<string, SDKBridgeSHandler>;
    _handleMessageFromNative?: (message: SDKBridgeNativeMessage) => void;
    [key: string]: SDKBridgeCallback<any> | undefined;
  }
}

// APP应用桥接
export class SDKBridge {
  // 构造函数
  public constructor() {
    window.dscb = 0;
    window._dsf = {};
    window._dsaf = {};
    window._handleMessageFromNative = (message: SDKBridgeNativeMessage): void => {
      const args = JSON.parse(message.data || "[]") as Array<unknown>;

      if (window._dsf[message.method]) {
        const result: SDKBridgeNativeReturn = {
          id: message.callbackId,
          complete: true,
          data: window._dsf[message.method](...args),
        };
        void this.invoke("_dsb.returnValue", result, { json: false });
        return;
      }
      if (window._dsaf[message.method]) {
        window._dsaf[message.method](...args, (data: unknown, complete = true): void => {
          const result: SDKBridgeNativeReturn = { id: message.callbackId, complete, data };
          void this.invoke("_dsb.returnValue", result, { json: false });
        });
      }
    };
  }

  // 环境支持
  public get support(): boolean {
    return !!(window._dsbridge ?? window._dswk ?? navigator.userAgent.includes("_dsbridge"));
  }

  // 调用原生方法
  public async invoke<T = any, R = any>(
    handler: string,
    args: T,
    options: { json: boolean },
  ): Promise<R> {
    return await new Promise<R>((resolve, reject) => {
      const payload = JSON.stringify({
        data: args ?? null,
        _dscbstub: `dscb${window.dscb}`,
      });

      window.dscb += 1;
      window[`dscb${window.dscb - 1}`] = (result: unknown) => {
        if (options.json) {
          resolve(JSON.parse(result as string) as R);
        } else {
          resolve(result as R);
        }
      };

      if (window._dsbridge) {
        const result = window._dsbridge.call(handler, payload);
        const { code } = JSON.parse(result || "{}") as { code: number };
        if (code === -1) {
          reject(new Error(`Native call failed: ${handler}-${JSON.stringify(args)}`));
        }
      }
      if (window._dswk || navigator.userAgent.includes("_dsbridge")) {
        const result = window.prompt(`_dsbridge=${handler}`, payload)!;
        const { code } = JSON.parse(result || "{}") as { code: number };
        if (code === -1) {
          reject(new Error(`Native call failed: ${handler}-${JSON.stringify(args)}`));
        }
      }
    });
  }

  // 订阅原生事件
  public subscribe(
    handler: string,
    callback: SDKBridgeSHandler,
    options: { async: boolean },
  ): void {
    if (!window._dsInit) {
      window._dsInit = true;
      setTimeout(() => {
        void this.invoke("_dsb.dsinit", null, { json: false });
      }, 0);
    }
    if (options.async) {
      window._dsaf[handler] = callback;
    } else {
      window._dsf[handler] = callback;
    }
  }

  // 取消订阅原始事件
  public unsubscribe(
    handler: string,
    callback: SDKBridgeSHandler,
    options: { async: boolean },
  ): void {
    if (!window._dsInit) return;
    if (options.async && window._dsaf[handler] === callback) {
      delete window._dsaf[handler];
    }
    if (!options.async && window._dsf[handler] === callback) {
      delete window._dsf[handler];
    }
  }
}

export type * from "./type";
